import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { createSpecialOffer, fetchSpecialOffers, updateSpecialOffer, deleteSpecialOffer } from '../store/specialOffersSlice.js';
import { fetchProducts } from '../store/productsSlice.js';

const AdminSpecialOffers = () => {
  const dispatch = useDispatch();
  const specialOffers = useSelector(s => s.specialOffers.items);
  const products = useSelector(s => s.products.items);
  
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    theme: "flash-sale",
    discount: "",
    selectedProducts: [],
    isActive: true,
    validUntil: "",
    description: ""
  });

  const themes = [
    { id: "flash-sale", name: "Flash Sale", color: "#ff4757", icon: "⚡" },
    { id: "weekend-deal", name: "Weekend Deal", color: "#3742fa", icon: "🎯" },
    { id: "mega-offer", name: "Mega Offer", color: "#2ed573", icon: "🔥" },
    { id: "limited-time", name: "Limited Time", color: "#ff6348", icon: "⏰" },
    { id: "special-discount", name: "Special Discount", color: "#a55eea", icon: "💎" }
  ];

  useEffect(() => {
    dispatch(fetchSpecialOffers());
    dispatch(fetchProducts());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProductSelect = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.includes(productId)
        ? prev.selectedProducts.filter(id => id !== productId)
        : [...prev.selectedProducts, productId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.discount || formData.selectedProducts.length === 0) {
      toast.error("Please fill all required fields and select at least one product");
      return;
    }

    try {
      if (editingOffer) {
        await dispatch(updateSpecialOffer({
          offerId: editingOffer.id,
          offer: formData
        })).unwrap();
        toast.success("Special offer updated successfully");
      } else {
        await dispatch(createSpecialOffer(formData)).unwrap();
        toast.success("Special offer created successfully");
      }
      
      resetForm();
    } catch (error) {
      toast.error("Failed to save special offer");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      subtitle: "",
      theme: "flash-sale",
      discount: "",
      selectedProducts: [],
      isActive: true,
      validUntil: "",
      description: ""
    });
    setShowForm(false);
    setEditingOffer(null);
  };

  const handleEdit = (offer) => {
    setFormData(offer);
    setEditingOffer(offer);
    setShowForm(true);
  };

  const handleDelete = async (offerId) => {
    if (window.confirm("Are you sure you want to delete this special offer?")) {
      try {
        await dispatch(deleteSpecialOffer(offerId)).unwrap();
        toast.success("Special offer deleted successfully");
      } catch (error) {
        toast.error("Failed to delete special offer");
      }
    }
  };

  const getSelectedTheme = () => themes.find(t => t.id === formData.theme); // eslint-disable-line no-unused-vars

  return (
    <div className="container py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold">
          <i className="bi bi-star me-2 text-warning"></i>
          Special Offers Management
        </h2>
        <button
          className="btn btn-primary rounded-pill px-4"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add Special Offer
        </button>
      </div>

      {showForm && (
        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
          <h5 className="fw-bold mb-3">
            {editingOffer ? "Edit Special Offer" : "Create Special Offer"}
          </h5>
          
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label fw-semibold">Offer Title *</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Flash Sale - 50% Off"
                  required
                />
              </div>
              
              <div className="col-md-4">
                <label className="form-label fw-semibold">Discount % *</label>
                <input
                  type="number"
                  className="form-control rounded-3"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  placeholder="50"
                  min="1"
                  max="90"
                  required
                />
              </div>
              
              <div className="col-md-6">
                <label className="form-label fw-semibold">Valid Until</label>
                <input
                  type="date"
                  className="form-control rounded-3"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Banner Subtitle</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  placeholder="e.g., Exclusive deals on premium fragrances · Limited stock"
                />
                <small className="text-muted">This line appears below the offer title on the banner</small>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Description</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the offer"
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Theme *</label>
                <div className="row g-2">
                  {themes.map(theme => (
                    <div key={theme.id} className="col-md-2">
                      <input
                        type="radio"
                        className="btn-check"
                        name="theme"
                        id={theme.id}
                        value={theme.id}
                        checked={formData.theme === theme.id}
                        onChange={handleInputChange}
                      />
                      <label
                        className="btn btn-outline-secondary w-100 rounded-3 p-3"
                        htmlFor={theme.id}
                        style={{
                          borderColor: formData.theme === theme.id ? theme.color : '',
                          backgroundColor: formData.theme === theme.id ? theme.color + '20' : ''
                        }}
                      >
                        <div className="text-center">
                          <div style={{ fontSize: '24px' }}>{theme.icon}</div>
                          <small className="fw-semibold">{theme.name}</small>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Select Products *</label>
                <div className="card bg-light rounded-3 p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <div className="row g-2">
                    {products.map(product => (
                      <div key={product.id} className="col-md-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`product-${product.id}`}
                            checked={formData.selectedProducts.includes(product.id)}
                            onChange={() => handleProductSelect(product.id)}
                          />
                          <label className="form-check-label small" htmlFor={`product-${product.id}`}>
                            {product.name} - ₹{product.price}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <small className="text-muted">Selected: {formData.selectedProducts.length} products</small>
              </div>

              <div className="col-12">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="isActive"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="isActive">
                    Active Offer
                  </label>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-success rounded-pill px-4">
                <i className="bi bi-check-circle me-2"></i>
                {editingOffer ? "Update Offer" : "Create Offer"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="row g-4">
        {specialOffers.map(offer => {
          const theme = themes.find(t => t.id === offer.theme);
          return (
            <div key={offer.id} className="col-md-6">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div 
                  className="card-header text-white p-3"
                  style={{ backgroundColor: theme?.color || '#6c757d' }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0 fw-bold">{theme?.icon} {offer.title}</h6>
                      <small className="opacity-75">{theme?.name}</small>
                    </div>
                    <div className="text-end">
                      <div className="badge bg-white text-dark fw-bold fs-6">
                        {offer.discount}% OFF
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small text-muted">Products: {offer.selectedProducts?.length || 0}</span>
                    <span className={`badge ${offer.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {offer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {offer.description && (
                    <p className="small text-muted mb-2">{offer.description}</p>
                  )}
                  
                  {offer.validUntil && (
                    <div className="small text-muted mb-3">
                      <i className="bi bi-calendar me-1"></i>
                      Valid until: {new Date(offer.validUntil).toLocaleDateString()}
                    </div>
                  )}
                  
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary rounded-pill flex-grow-1"
                      onClick={() => handleEdit(offer)}
                    >
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger rounded-pill"
                      onClick={() => handleDelete(offer.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {specialOffers.length === 0 && (
        <div className="text-center py-5">
          <i className="bi bi-star display-4 text-muted mb-3"></i>
          <h5 className="text-muted">No special offers created yet</h5>
          <p className="text-muted">Create your first special offer to boost sales</p>
        </div>
      )}
    </div>
  );
};

export default AdminSpecialOffers;